import styled from "styled-components";
import type { PropRuntimeGraph } from "../../game/assets/shared/types/PropRuntimeGraph";

declare const require: {
  context(
    path: string,
    deep?: boolean,
    filter?: RegExp,
  ): {
    keys(): string[];
    <T = string>(id: string): T;
  };
};

const propImageContext = require.context("../../assets/PropsImages", false, /\.(png|jpe?g|webp)$/i);

const propImageUrlMap = propImageContext.keys().reduce<Record<string, string>>((map, key) => {
  const fileName = key.replace(/^\.\//, "");
  const assetId = fileName.replace(/\.[^.]+$/, "");
  map[assetId.toLowerCase()] = propImageContext<string>(key);
  return map;
}, {});

export function getPropImageUrl(assetId?: string): string | undefined {
  if (!assetId) return undefined;
  return propImageUrlMap[assetId.toLowerCase()];
}

export function getSandboxRarityBackground(rarity?: number, empty?: boolean): string {
  if (empty) return "#141d2b";
  if (rarity === 2) return "#143324";
  if (rarity === 3) return "#142a46";
  if (rarity === 4) return "#2d2048";
  return "#1b2432";
}

export function getSandboxRarityBorder(rarity?: number, empty?: boolean): string {
  if (empty) return "#26364f";
  if (rarity === 2) return "#2f6b4f";
  if (rarity === 3) return "#2f5c96";
  if (rarity === 4) return "#6a4aa0";
  return "#34445f";
}

export interface PropImageProps {
  graph?: PropRuntimeGraph;
  empty?: boolean;
  size?: number;
}

export function PropImage({ graph, empty, size = 34 }: PropImageProps) {
  const imageUrl = getPropImageUrl(graph?.assetId);
  return (
    <PropImageFrame $size={size} $empty={empty}>
      {imageUrl ? (
        <PropImageElement src={imageUrl} alt={graph?.name || graph?.assetId || "prop"} draggable={false} />
      ) : (
        <PropImageFallback>{empty ? "" : (graph?.name || graph?.assetId || "-").slice(0, 1)}</PropImageFallback>
      )}
    </PropImageFrame>
  );
}

const PropImageFrame = styled.div<{ $size: number; $empty?: boolean }>`
  flex: 0 0 ${(props) => props.$size}px;
  width: ${(props) => props.$size}px;
  height: ${(props) => props.$size}px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: ${(props) => props.$empty ? "#1a2535" : "rgba(255, 255, 255, .10)"};
  overflow: hidden;
`;

const PropImageElement = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  user-select: none;
  pointer-events: none;
`;

const PropImageFallback = styled.span`
  color: #cbd5e1;
  font-size: 12px;
  font-weight: 700;
`;
